/**
 * Merchant Time Slots API
 *
 * The merchant's pickup slot is owned by CORE
 * (`merchant_settings.pickup_time_slot_code`) and is still READ from there. It is
 * no longer read-only: `setMerchantSlot` writes it through core's slot service
 * (`PUT /pickup-slots/merchant-preference`), which validates the code against the
 * live grid, busts core's settings cache, and re-slots the merchant's open
 * pickups — hence the `reslotted` counts that come back and must be shown.
 *
 * The dedicated-PA binding is fleet-owned and still writes through
 * `merchantPickupWindows.api`.
 */

import api from './api';
import type { TimeWindow } from './merchantPickupWindows.api';

export interface MerchantSlot {
  merchant_id: number;
  merchant_uid: string | null;
  merchant_name: string;

  /** Core: S1..S5, or null when the merchant has no slot set. */
  slot_code: string | null;
  slot_label: string | null;
  slot_window: { from: string; to: string } | null;
  slot_active: boolean | null;
  preferred_timing: string | null;
  auto_select_next_slot: boolean;
  recurring_pickup_enabled: boolean;
  bulk_pickup_enabled: boolean;
  bulk_rto_enabled: boolean;
  delay_rto_and_resend_enabled: boolean;

  /** Fleet: the dedicated-PA binding. null doc id means nothing is stored yet. */
  fleet_doc_id: string | null;
  requires_dedicated_driver: boolean;
  dedicated_driver_uid: string | null;
  dedicated_driver_name: string | null;
  /** false = the binding exists but routing ignores it (inactive doc). */
  fleet_doc_active: boolean | null;
  notes: string | null;

  /** Retired free-form windows, kept visible for provenance only. */
  legacy_time_windows: TimeWindow[];
}

export interface MerchantSlotsMeta {
  source: string;
  slot_config: { source: string; fetched_at: string | null; timezone?: string };
  dedicated_without_driver: number;
}

export async function getMerchantSlots(
  includeUnset = false
): Promise<{ rows: MerchantSlot[]; meta: MerchantSlotsMeta }> {
  const response = await api.get<{ success: boolean; data: MerchantSlot[]; meta: MerchantSlotsMeta }>(
    `/merchants/slots${includeUnset ? '?includeUnset=true' : ''}`
  );
  return { rows: response.data.data, meta: response.data.meta };
}

/** How many of the merchant's open pickups core moved as a side effect. */
export interface ReslottedReport {
  moved: number;
  skipped: number;
  relocated: number;
}

export interface SetMerchantSlotPayload {
  merchant_uid: string;
  time_slot_code: string;
  /** Required by core and persisted on the audit row — not merely echoed. */
  reason: string;
  preferred_timing?: 'early' | 'mid' | 'late';
  auto_select_next_slot?: boolean;
  recurring_pickup_enabled?: boolean;
}

/**
 * Changing a default can move live pickups — `relocated` ones were already on a
 * route and Fleet will re-plan them. Callers must surface these numbers rather
 * than reporting a bare success.
 */
export async function setMerchantSlot(
  payload: SetMerchantSlotPayload
): Promise<{ reslotted: ReslottedReport | null }> {
  const response = await api.put<{ success: boolean; reslotted: ReslottedReport | null }>(
    // Sits with the merchants read, not under /pickup-slots: that router is behind
    // the slot-pipeline feature gate, and merchant config must not be.
    '/merchants/slots/preference',
    payload
  );
  return { reslotted: response.data.reslotted ?? null };
}

/** Stable slot ordering + colour, so the table, chips and timeline all agree. */
export const SLOT_ORDER = ['S1', 'S2', 'S3', 'S4', 'S5'] as const;

export const SLOT_STYLE: Record<string, { badge: string; bar: string; hex: string }> = {
  S1: { badge: 'bg-sky-100 text-sky-700 border-sky-200', bar: 'bg-sky-500', hex: '#0ea5e9' },
  S2: { badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', bar: 'bg-emerald-500', hex: '#10b981' },
  S3: { badge: 'bg-amber-100 text-amber-700 border-amber-200', bar: 'bg-amber-500', hex: '#f59e0b' },
  S4: { badge: 'bg-violet-100 text-violet-700 border-violet-200', bar: 'bg-violet-500', hex: '#8b5cf6' },
  S5: { badge: 'bg-indigo-100 text-indigo-700 border-indigo-200', bar: 'bg-indigo-500', hex: '#6366f1' },
};

export const UNSET_STYLE = { badge: 'bg-gray-100 text-gray-500 border-gray-200', bar: 'bg-gray-300', hex: '#d1d5db' };

export function slotStyle(code: string | null) {
  return (code && SLOT_STYLE[code]) || UNSET_STYLE;
}
